import { Children, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { hoy } from '@abastece/shared';
import type { FormaPagoVenta } from '@abastece/core';
import { PERIODOS, etiquetaRango, mesDe, otroMes, semanasDelMes, tocarDia, type Periodo } from '../lib/historial.js';
import { coincide, puntaje } from '../lib/texto.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** A way of paying, as the screens name it: fiado for `credito`. */
export const claveDeForma = (forma: FormaPagoVenta): Clave => (forma === 'credito' ? 'pago.fiado' : (`pago.${forma}` as Clave));

/**
 * The box over a history list (`lib/historial.ts`), with each condition in sight under it (the owner,
 * 2026-10-06: «can the filters be separated into individual buttons/dropdowns instead of just one
 * filter button that expands into a bunch of dropdowns?»): the days as buttons with a calendar
 * (`FiltroFechas`), a person or a supplier found by typing (`BuscarEnLista`), the rest as dropdowns
 * (`FilaDesplegables`). Those set are lit and say what they hold; «Quitar los filtros» shows while
 * anything is.
 */
export function FiltrosEnLinea({
  buscar,
  hayPuestos,
  onQuitarTodos,
  children,
}: {
  /** The box, and what it looks through; none where a record has no words of its own, as a till's shift. */
  buscar?: { texto: string; onTexto: (texto: string) => void; placeholder: string };
  /** Whether any condition holds something: «Quitar los filtros» shows then. */
  hayPuestos: boolean;
  onQuitarTodos: () => void;
  /** The conditions, a row each. */
  children: ReactNode;
}) {
  const t = useT();
  const id = useId();
  return (
    <div className="mb-4 flex flex-col gap-3" data-historial data-historial-en-linea>
      {buscar && (
        <div>
          <label htmlFor={id} className="etiqueta">
            {t('comun.buscar')}
          </label>
          <input
            id={id}
            className="campo"
            type="search"
            enterKeyHint="search"
            value={buscar.texto}
            onChange={(e) => buscar.onTexto(e.target.value)}
            placeholder={buscar.placeholder}
            autoComplete="off"
          />
        </div>
      )}
      {children}
      {hayPuestos && (
        <button type="button" className="btn-texto gap-1 self-start text-xs text-slate-400" onClick={onQuitarTodos}>
          <Ico icono="reiniciar" className="size-3.5" />
          {t('historial.quitarTodos')}
        </button>
      )}
    </div>
  );
}

/** The screens with a whole history, whose latest few another screen keeps (`lib/historialDinero.ts`). */
export type HistorialEntero = '/gastos/pagos' | '/gastos/dueno' | '/caja/turnos' | '/recargas/anteriores' | '/efectivo/depositos';

/** «Ver todos» over a list of the latest few: the screen with all of it, and the filters to find one. */
export function VerTodos({ to, femenino = false }: { to: HistorialEntero; femenino?: boolean }) {
  const t = useT();
  return (
    <Link to={to} className="btn-texto shrink-0 gap-0.5 text-xs text-marca-300" data-ver-todos={to}>
      {t(femenino ? 'historial.verTodas' : 'historial.verTodos')}
      <Ico icono="adelante" className="size-3.5" />
    </Link>
  );
}

/** On a wider screen, all in one row: whole class names, for Tailwind to find them. */
const EN_UNA_FILA: Record<number, string> = { 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' };

/** Dropdowns two to a row on a phone, all in one on a wider screen; one alone, the whole row, or its name is cut in half. */
export function FilaDesplegables({ children }: { children: ReactNode }) {
  const cuantos = Children.toArray(children).length;
  return (
    <div className={`grid gap-2 ${cuantos === 1 ? 'grid-cols-1' : 'grid-cols-2'} ${EN_UNA_FILA[cuantos] ?? ''}`} data-historial-desplegables>
      {children}
    </div>
  );
}

/**
 * One condition as a dropdown of its own: its name until something is picked, then what was
 * picked, lit. The select lies over the whole of it unseen, so a tap opens the phone's own list,
 * and a screen reader hears the name and what is chosen. The list it opens is dark, as the app is:
 * left to itself, a computer drew the options in the pill's white on its own white.
 */
export function Desplegable({
  clave,
  etiqueta,
  value,
  onChange,
  elegido,
  children,
}: {
  /** Which condition: the screen's own name for it, never shown. */
  clave: string;
  /** Its name, said while nothing is picked and always to a screen reader. */
  etiqueta: string;
  value: string;
  onChange: (value: string) => void;
  /** What it says once something is picked; undefined while nothing is. */
  elegido: string | undefined;
  /** Its options, the first being «any». */
  children: ReactNode;
}) {
  const puesto = elegido !== undefined;
  return (
    <label
      className={`relative flex h-11 min-w-0 items-center justify-between gap-1.5 rounded-xl px-3 text-sm ring-1 ring-inset transition has-[select:focus-visible]:ring-2 has-[select:focus-visible]:ring-marca-400 ${
        puesto ? 'bg-marca-600/20 font-medium text-white ring-marca-500/60 hover:bg-marca-600/30' : 'bg-slate-800 text-slate-300 ring-white/5 hover:bg-slate-700 hover:text-slate-100'
      }`}
      data-desplegable={clave}
      data-puesto={puesto ? '' : undefined}
    >
      {/* Heard from the select itself, its name and what is chosen: not twice. */}
      <span className="truncate" aria-hidden>
        {elegido ?? etiqueta}
      </span>
      <Ico icono="desplegar" className="size-4 shrink-0 text-slate-400" />
      {/* At 16 px, or a phone zooms in on it. */}
      <select
        aria-label={etiqueta}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer bg-slate-900 text-base text-slate-100 opacity-0 [color-scheme:dark]"
      >
        {children}
      </select>
    </label>
  );
}

/**
 * Which days: the periods with a name as a row of buttons, as «Contabilidad» has them (the owner,
 * 2026-10-06: «instead of a damn dropdown it should be canned "yesterday", "last month" etc like the
 * accounting filters»), and «Elegir fechas», which opens a calendar and then says the days picked.
 * `ayuda` says what day each record goes by, where that is not obvious, as on an order.
 */
export function FiltroFechas({
  periodo,
  desde,
  hasta,
  ayuda,
  onCambio,
}: {
  periodo: Periodo;
  desde: string;
  hasta: string;
  ayuda?: string;
  onCambio: (cambio: { periodo: Periodo; desde: string; hasta: string }) => void;
}) {
  const t = useT();
  const hoyEs = hoy();
  const [calendario, setCalendario] = useState(false);
  const boton = (activo: boolean) =>
    `inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition ${
      activo ? 'bg-marca-600 font-medium text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
    }`;
  return (
    <div className="flex flex-col gap-1.5">
      {/* Wrapping like «Contabilidad»'s, never sliding sideways: a scroll bar under them read as broken. */}
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('historial.periodo')} data-fechas>
        {PERIODOS.filter((p) => p !== 'elegir').map((p) => (
          <button
            key={p}
            type="button"
            data-sin-icono="pestana"
            data-periodo={p}
            aria-pressed={periodo === p}
            onClick={() => onCambio({ periodo: p, desde: '', hasta: '' })}
            className={boton(periodo === p)}
          >
            {t(`historial.periodo.${p}`)}
          </button>
        ))}
        <button
          type="button"
          data-periodo="elegir"
          aria-pressed={periodo === 'elegir'}
          onClick={() => setCalendario(true)}
          className={boton(periodo === 'elegir')}
        >
          <Ico icono="calendario" className="size-4" />
          {periodo === 'elegir' && (desde || hasta) ? etiquetaRango(desde, hasta, hoyEs, localeActual()) : t('historial.periodo.elegir')}
        </button>
      </div>
      {ayuda && <p className="text-xs text-slate-500">{ayuda}</p>}
      {calendario && (
        <Calendario
          desde={periodo === 'elegir' ? desde : ''}
          hasta={periodo === 'elegir' ? hasta : ''}
          hoyEs={hoyEs}
          onListo={(d, h) => {
            onCambio({ periodo: 'elegir', desde: d, hasta: h });
            setCalendario(false);
          }}
          onCerrar={() => setCalendario(false)}
        />
      )}
    </div>
  );
}

/**
 * Two dates picked on a month's calendar: the first day tapped, then the last, the days between
 * lit; «Listo» with one alone is that day. Nothing after today, which has nothing in it yet.
 */
export function Calendario({
  desde,
  hasta,
  hoyEs,
  onListo,
  onCerrar,
}: {
  desde: string;
  hasta: string;
  hoyEs: string;
  onListo: (desde: string, hasta: string) => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const locale = localeActual();
  const [elegido, setElegido] = useState({ desde, hasta });
  const [mes, setMes] = useState(mesDe(desde || hoyEs));
  const titulo = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${mes}-01T00:00:00Z`));
  // 2026-02-01 was a Sunday: the week as the calendar lays it out.
  const diasSemana = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 1, 1 + i))),
  );
  const completa = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' });
  const extremo = (d: string) => d === elegido.desde || d === elegido.hasta;
  const entre = (d: string) => Boolean(elegido.desde && elegido.hasta && elegido.desde < d && d < elegido.hasta);

  return (
    <Modal
      titulo={t('historial.periodo.elegir')}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          <p className="text-center text-sm text-slate-400" data-calendario-dice>
            {!elegido.desde
              ? t('historial.calendario.primero')
              : !elegido.hasta
                ? t('historial.calendario.ultimo', { dia: etiquetaRango(elegido.desde, '', hoyEs, locale) })
                : etiquetaRango(elegido.desde, elegido.hasta, hoyEs, locale)}
          </p>
          <button
            type="button"
            className="btn-primario w-full"
            disabled={!elegido.desde}
            onClick={() => onListo(elegido.desde, elegido.hasta || elegido.desde)}
            data-calendario-listo
          >
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        </div>
      }
    >
      <div data-calendario data-mes={mes}>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="btn-secundario h-11 min-h-0 w-11 px-0"
            aria-label={t('historial.calendario.anterior')}
            onClick={() => setMes(otroMes(mes, -1))}
            data-calendario-anterior
          >
            <Ico icono="atras" className="size-5" />
          </button>
          <p className="font-medium first-letter:uppercase" data-calendario-mes>
            {titulo}
          </p>
          <button
            type="button"
            className="btn-secundario h-11 min-h-0 w-11 px-0"
            aria-label={t('historial.calendario.siguiente')}
            disabled={mes >= mesDe(hoyEs)}
            onClick={() => setMes(otroMes(mes, 1))}
            data-calendario-siguiente
          >
            <Ico icono="adelante" className="size-5" />
          </button>
        </div>
        <div className="mt-3 grid grid-cols-7 gap-1 text-center">
          {diasSemana.map((d, i) => (
            <span key={i} className="py-1 text-xs font-medium text-slate-500 uppercase" aria-hidden>
              {d}
            </span>
          ))}
          {semanasDelMes(mes)
            .flat()
            .map((d, i) =>
              d === null ? (
                <span key={`vacio-${i}`} />
              ) : (
                <button
                  key={d}
                  type="button"
                  data-sin-icono="tecla"
                  data-dia={d}
                  disabled={d > hoyEs}
                  aria-pressed={extremo(d) || entre(d)}
                  aria-label={completa.format(new Date(`${d}T00:00:00Z`))}
                  onClick={() => setElegido((e) => tocarDia(e, d))}
                  className={`tabular h-10 rounded-lg text-sm transition disabled:text-slate-700 ${
                    extremo(d)
                      ? 'bg-marca-600 font-semibold text-white'
                      : entre(d)
                        ? 'bg-marca-600/25 text-white'
                        : d === hoyEs
                          ? 'text-slate-100 ring-1 ring-marca-500/60 ring-inset'
                          : 'text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  {Number(d.slice(8))}
                </button>
              ),
            )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * The names the list under the box draws at a time, fifty more as it is scrolled near its foot: it
 * drew eight, and a shop's ninth supplier by name was out of reach but by typing (2026-10-12).
 */
const NOMBRES_A_LA_VEZ = 50;

/**
 * A supplier or a client found by typing (the owner, 2026-10-06: «the supplier should be a search
 * field with automatch»): what matches comes up under the box as it is typed, best first, accents
 * aside, and the arrows and Enter pick as a tap does. Picked, it is lit with the name and an ✕.
 */
export function BuscarEnLista({
  clave,
  etiqueta,
  placeholder,
  opciones,
  value,
  onChange,
}: {
  /** Which condition: the screen's own name for it, never shown. */
  clave: string;
  /** What is being found, «Proveedor»: said beside the one picked and to a screen reader. */
  etiqueta: string;
  placeholder: string;
  opciones: readonly { id: string; nombre: string }[];
  /** The one picked; empty for any. */
  value: string;
  onChange: (id: string) => void;
}) {
  const t = useT();
  const id = useId();
  const [texto, setTexto] = useState('');
  const [abierta, setAbierta] = useState(false);
  const [activa, setActiva] = useState(0);
  const [cuantas, setCuantas] = useState(NOMBRES_A_LA_VEZ);
  const halladas = useMemo(() => {
    const q = texto.trim();
    const porNombre = (a: { nombre: string }, b: { nombre: string }) => a.nombre.localeCompare(b.nombre, 'es');
    return q
      ? opciones.filter((o) => coincide(o.nombre, q)).sort((a, b) => puntaje(a.nombre, q) - puntaje(b.nombre, q) || porNombre(a, b))
      : [...opciones].sort(porNombre);
  }, [opciones, texto]);
  const vistas = halladas.slice(0, cuantas);
  // The one the arrows reached stays in sight, the box scrolled and never the page: with fifty in it,
  // the arrows go well past what it shows.
  useEffect(() => {
    const caja = document.getElementById(`${id}-lista`);
    const op = document.getElementById(`${id}-${activa}`);
    if (!abierta || !caja || !op) return;
    if (op.offsetTop < caja.scrollTop) caja.scrollTop = op.offsetTop;
    else if (op.offsetTop + op.offsetHeight > caja.scrollTop + caja.clientHeight) caja.scrollTop = op.offsetTop + op.offsetHeight - caja.clientHeight;
  }, [abierta, activa, id]);

  if (value) {
    const nombre = opciones.find((o) => o.id === value)?.nombre ?? '…';
    return (
      <div
        className="flex h-11 min-w-0 items-center rounded-xl bg-marca-600/20 pl-3 text-sm font-medium text-white ring-1 ring-marca-500/60 ring-inset"
        data-buscar-en-lista={clave}
        data-puesto=""
      >
        <span className="min-w-0 flex-1 truncate" data-elegido>
          <span className="font-normal text-slate-300">{etiqueta}: </span>
          {nombre}
        </span>
        <button
          type="button"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-r-xl text-slate-300 transition hover:bg-white/10 hover:text-white"
          aria-label={t('historial.quitar', { filtro: nombre })}
          onClick={() => onChange('')}
        >
          <Ico icono="cerrar" className="size-4" />
        </button>
      </div>
    );
  }

  const elegir = (o: { id: string }) => {
    onChange(o.id);
    setTexto('');
    setAbierta(false);
  };
  return (
    <div className="relative" data-buscar-en-lista={clave}>
      <Ico icono="buscar" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-500" />
      {/* At 16 px, or a phone zooms in on it. */}
      <input
        role="combobox"
        aria-label={etiqueta}
        aria-expanded={abierta}
        aria-controls={`${id}-lista`}
        aria-autocomplete="list"
        aria-activedescendant={abierta && vistas[activa] ? `${id}-${activa}` : undefined}
        className="h-11 w-full rounded-xl bg-slate-800 pr-3 pl-9 text-base text-slate-100 ring-1 ring-white/5 transition ring-inset placeholder:text-slate-400 hover:ring-slate-500 focus:ring-2 focus:ring-marca-500 focus:outline-none"
        value={texto}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierta(true);
          setActiva(0);
          setCuantas(NOMBRES_A_LA_VEZ);
        }}
        onFocus={() => setAbierta(true)}
        onBlur={() => setAbierta(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setAbierta(true);
            const siguiente = Math.min(activa + 1, halladas.length - 1);
            setActiva(siguiente);
            if (siguiente >= cuantas) setCuantas(cuantas + NOMBRES_A_LA_VEZ);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiva((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            const o = vistas[activa];
            if (abierta && o) {
              e.preventDefault();
              elegir(o);
            }
          } else if (e.key === 'Escape') {
            setAbierta(false);
          }
        }}
      />
      {abierta && (
        <ul
          id={`${id}-lista`}
          role="listbox"
          aria-label={etiqueta}
          className="absolute top-full right-0 left-0 z-20 mt-1 max-h-64 overflow-auto rounded-xl bg-slate-900 p-1 shadow-xl ring-1 ring-slate-700"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (cuantas < halladas.length && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) setCuantas(cuantas + NOMBRES_A_LA_VEZ);
          }}
        >
          {halladas.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-slate-500">{t('historial.ningunoSeLlamaAsi')}</li>
          ) : (
            vistas.map((o, i) => (
              <li
                key={o.id}
                id={`${id}-${i}`}
                role="option"
                aria-selected={i === activa}
                // Picked before the box loses the cursor, or the list is gone under the finger.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => elegir(o)}
                className={`cursor-pointer truncate rounded-lg px-3 py-2.5 text-sm ${i === activa ? 'bg-slate-800 text-white' : 'text-slate-200 hover:bg-slate-800/60'}`}
              >
                {o.nombre}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
